import React, { useCallback, useEffect, useState } from 'react';
import {
  Loader2, Pin, PinOff, Plus, StickyNote, Trash2, X,
} from 'lucide-react';
import client from '../lib/api';
import { useToast } from '../hooks/use-toast';
import ConfirmDialog from './components/ConfirmDialog';
import { formatDateTime } from './lib/formatters';
import { useAdminPermissions } from './lib/permissions';

const MAX_LENGTH = 4000;

/** Üye profilindeki yönetici notları kartı.
 *
 * Notlar audit log'dan ayrıdır: audit "kim ne yaptı"yı otomatik yazar, buradaki
 * metni yönetici kendi gözlemi için girer. Sabitlenen not listenin başında
 * kalır — sık bakılan uyarılar (şüpheli hesap, özel anlaşma) aşağı kaymasın.
 */
const UserNotes = ({ user }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState('');
  const [composing, setComposing] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const { toast } = useToast();
  const { can } = useAdminPermissions();
  const canWrite = can('users.write');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get(`/admin/users/${user.id}/notes`);
      setNotes(data.items || []);
    } catch (error) {
      toast({
        title: 'Notlar alınamadı',
        description: error.response?.data?.detail || 'Bir hata oluştu',
      });
    } finally { setLoading(false); }
  }, [toast, user.id]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const body = draft.trim();
    if (!body) {
      toast({ title: 'Not boş olamaz' });
      return;
    }
    setBusy(true);
    try {
      await client.post(`/admin/users/${user.id}/notes`, { body });
      toast({ title: 'Not eklendi' });
      setDraft('');
      setComposing(false);
      await load();
    } catch (error) {
      toast({
        title: 'Not eklenemedi',
        description: error.response?.data?.detail || 'Bir hata oluştu',
      });
    } finally { setBusy(false); }
  };

  const togglePin = async (note) => {
    setBusy(true);
    try {
      await client.patch(`/admin/users/${user.id}/notes/${note.id}`, {
        pinned: !note.pinned,
      });
      await load();
    } catch (error) {
      toast({
        title: 'Not güncellenemedi',
        description: error.response?.data?.detail || 'Bir hata oluştu',
      });
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!removeTarget) return;
    setBusy(true);
    try {
      await client.delete(`/admin/users/${user.id}/notes/${removeTarget.id}`);
      toast({ title: 'Not silindi' });
      setRemoveTarget(null);
      await load();
    } catch (error) {
      toast({
        title: 'Not silinemedi',
        description: error.response?.data?.detail || 'Bir hata oluştu',
      });
    } finally { setBusy(false); }
  };

  return (
    <section id="user-notes" className="admin-card overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 border-b border-white/5 p-5">
        <StickyNote size={17} className="text-amber-400" />
        <div>
          <h2 className="font-bold">Yönetici Notları</h2>
          <p className="text-xs text-slate-600">
            Bu üye hakkındaki iç notlar. Üyeye gösterilmez.
          </p>
        </div>
        {notes.length > 0 && (
          <span className="text-xs text-slate-600">{notes.length} not</span>
        )}
        {canWrite && !composing && (
          <button
            type="button"
            onClick={() => setComposing(true)}
            className="admin-btn-primary ml-auto inline-flex items-center gap-2"
          >
            <Plus size={14} /> Not Ekle
          </button>
        )}
      </div>

      {composing && (
        <div className="space-y-2 border-b border-white/5 bg-white/[0.015] p-4">
          <textarea
            value={draft}
            maxLength={MAX_LENGTH}
            autoFocus
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Örn. Telefonla görüşüldü, çekim hesabı doğrulandı."
            className="admin-input min-h-24"
          />
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-slate-600">
              {draft.length} / {MAX_LENGTH}
            </span>
            <button
              type="button"
              disabled={busy}
              onClick={save}
              className="admin-btn-primary ml-auto inline-flex items-center gap-2"
            >
              {busy && <Loader2 size={14} className="animate-spin" />} Kaydet
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => { setComposing(false); setDraft(''); }}
              className="admin-btn-secondary inline-flex items-center gap-2"
            >
              <X size={14} /> Vazgeç
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-10 text-center">
          <Loader2 className="inline animate-spin text-cyan-400" />
        </div>
      ) : (
        <div className="divide-y divide-white/[0.04]">
          {notes.map((note) => (
            <article key={note.id} className="flex items-start gap-3 p-4">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                  {note.pinned && (
                    <span className="badge-pill badge-yellow inline-flex items-center gap-1">
                      <Pin size={10} /> Sabit
                    </span>
                  )}
                  <span className="font-semibold text-slate-400">
                    {note.admin_username || 'Yönetici'}
                  </span>
                  <span>{formatDateTime(note.created_at)}</span>
                </div>
                <p className="mt-2 whitespace-pre-wrap break-words text-sm text-slate-200">
                  {note.body}
                </p>
              </div>
              {canWrite && (
                <div className="flex shrink-0 gap-2">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => togglePin(note)}
                    className="icon-btn view"
                    title={note.pinned ? 'Sabitlemeyi kaldır' : 'Başa sabitle'}
                  >
                    {note.pinned ? <PinOff size={13} /> : <Pin size={13} />}
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setRemoveTarget(note)}
                    className="icon-btn ban"
                    title="Notu sil"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              )}
            </article>
          ))}
          {!notes.length && (
            <div className="p-10 text-center text-slate-500">
              Bu üye için henüz not yok.
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        open={Boolean(removeTarget)}
        title="Not silinsin mi?"
        description="Silinen not geri getirilemez."
        confirmLabel="Sil"
        tone="danger"
        busy={busy}
        onCancel={() => setRemoveTarget(null)}
        onConfirm={remove}
      />
    </section>
  );
};

export default UserNotes;
